/**
 * Email API Service
 *
 * Handles interactions with email marketing endpoints
 * Mirrors the pattern from SMS API for consistency
 */
import { apiRequest } from './client';
import { supabase } from '@/integrations/supabase/client';

/**
 * Email Campaign interface
 */
export interface EmailCampaign {
  id: string;
  organisation_id: string;
  restaurant_id?: string | null;
  selected_restaurants?: string[];
  campaign_name: string;
  campaign_subject: string;
  campaign_content_html: string;
  campaign_content_text?: string;
  description?: string;
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed';
  audience_type: 'all' | 'filtered' | 'custom';
  audience_filters?: Record<string, any>;
  schedule_type: 'immediate' | 'scheduled';
  schedule_time?: string;
  template_id?: number | null;
  email_domain_id?: string | null;
  sender_email?: string;
  sender_name?: string;
  recipient_count?: number;
  sent_count?: number;
  opened_count?: number;
  clicked_count?: number;
  bounced_count?: number;
  unsubscribed_count?: number;
  failed_count?: number;
  actual_cost?: number;
  estimated_cost?: number;
  preview_image_url?: string | null;
  created_at?: string;
  updated_at?: string;
  sent_at?: string;
  personalisation_options?: Record<string, any>;
}

/**
 * Email Template interface
 */
export interface EmailTemplate {
  id: string;
  organisation_id?: string | null;
  restaurant_id?: string | null;
  is_system_template?: boolean;
  template_name: string;
  template_subject: string;
  template_content_html: string;
  template_content_text?: string;
  category?: string;
  template_description?: string;
  available_variables?: string[];
  preview_image_url?: string | null;
  // Template system fields (added 2025-10-22)
  design_json?: Record<string, any> | null;
  template_variables?: Record<string, any> | null;
  default_design_json?: Record<string, any> | null;
  template_version?: number;
  is_org_wide?: boolean;
  usage_count?: number;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
}

/**
 * Email Subscriber/Customer interface
 */
export interface EmailSubscriber {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  email_opt_in: boolean;
  email_opt_in_source?: string;
  email_opt_in_timestamp?: string;
  email_opt_out_timestamp?: string;
  last_email_sent?: string;
  total_emails_sent?: number;
  total_emails_opened?: number;
  total_emails_clicked?: number;
  is_bounced?: boolean;
  bounce_type?: string;
}

/**
 * Audience Preview interface
 */
export interface AudiencePreview {
  total_recipients: number;
  estimated_cost: number;
  sample_recipients: Array<{
    email: string;
    first_name?: string;
    last_name?: string;
    total_orders?: number;
  }>;
}

/**
 * Campaign list response interface
 */
interface CampaignListResponse {
  success: boolean;
  campaigns: EmailCampaign[];  // Backend returns "campaigns" not "data"
  total: number;
  page: number;
  per_page: number;
}

/**
 * Template list response interface
 */
interface TemplateListResponse {
  success: boolean;
  templates: EmailTemplate[];  // Backend returns "templates" not "data"
  total: number;
  page: number;
  per_page: number;
}

/**
 * Generic API response interface
 */
interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

/**
 * Email Service for interacting with email marketing APIs
 */
const EmailService = {
  /**
   * ==================== CAMPAIGN ENDPOINTS ====================
   */

  /**
   * Fetch email campaigns for an organisation
   */
  fetchCampaigns: async (
    organisationId: string,
    filters?: {
      restaurant_id?: string;
      status?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<EmailCampaign[]> => {
    try {
      console.log('[Email Campaigns] Fetching campaigns for organisation:', organisationId);

      const params = new URLSearchParams({
        organisation_id: organisationId,
      });

      if (filters?.restaurant_id) params.append('restaurant_id', filters.restaurant_id);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const response = await apiRequest<CampaignListResponse>({
        method: 'GET',
        url: `/api/email/campaigns?${params.toString()}`
      });

      if (response && response.success && response.campaigns) {
        console.log(`[Email Campaigns] Successfully fetched ${response.campaigns.length} campaigns`);
        return response.campaigns;
      }

      throw new Error('Failed to fetch campaigns');
    } catch (error) {
      console.error('[Email Campaigns] Error fetching campaigns:', error);
      throw error;
    }
  },

  /**
   * Get a single campaign by ID
   */
  getCampaign: async (campaignId: string): Promise<EmailCampaign> => {
    try {
      console.log('[Email Campaigns] Fetching campaign:', campaignId);

      const response = await apiRequest<{ success: boolean; campaign: EmailCampaign }>({
        method: 'GET',
        url: `/api/email/campaigns/${campaignId}`
      });

      if (response && response.success && response.campaign) {
        console.log('[Email Campaigns] Successfully fetched campaign');
        return response.campaign;
      }

      throw new Error('Failed to fetch campaign');
    } catch (error) {
      console.error('[Email Campaigns] Error fetching campaign:', error);
      throw error;
    }
  },

  /**
   * Create a new email campaign
   */
  createCampaign: async (campaignData: {
    organisation_id: string;
    restaurant_id?: string | null;
    selected_restaurants?: string[];
    campaign_name: string;
    campaign_subject: string;
    campaign_content_html: string;
    campaign_content_text?: string;
    description?: string;
    audience_type?: 'all' | 'filtered' | 'custom';
    audience_filters?: Record<string, any>;
    schedule_type?: 'immediate' | 'scheduled';
    schedule_time?: string;
    template_id?: number | null;
    email_domain_id?: string | null;
    sender_email?: string;
    sender_name?: string;
    personalisation_options?: Record<string, any>;
  }): Promise<EmailCampaign> => {
    try {
      console.log('[Email Campaigns] Creating campaign:', campaignData.campaign_name);

      // Validate required fields
      if (!campaignData.organisation_id) {
        throw new Error('Organisation ID is required');
      }
      if (!campaignData.campaign_name || !campaignData.campaign_subject) {
        throw new Error('Campaign name and subject are required');
      }
      if (!campaignData.campaign_content_html) {
        throw new Error('Campaign content is required');
      }

      const response = await apiRequest<{ success: boolean; campaign: EmailCampaign; error?: string }>({
        method: 'POST',
        url: '/api/email/campaigns',
        data: campaignData
      });

      if (response && response.success && response.campaign) {
        console.log('[Email Campaigns] Campaign created successfully:', response.campaign.id);
        return response.campaign;
      }

      throw new Error(response?.error || 'Failed to create campaign');
    } catch (error) {
      console.error('[Email Campaigns] Error creating campaign:', error);
      throw error;
    }
  },

  /**
   * Update an existing campaign
   */
  updateCampaign: async (
    campaignId: string,
    updates: Partial<EmailCampaign>
  ): Promise<EmailCampaign> => {
    try {
      console.log('[Email Campaigns] Updating campaign:', campaignId);

      const response = await apiRequest<{ success: boolean; campaign: EmailCampaign; error?: string }>({
        method: 'PUT',
        url: `/api/email/campaigns/${campaignId}`,
        data: updates
      });

      if (response && response.success && response.campaign) {
        console.log('[Email Campaigns] Campaign updated successfully');
        return response.campaign;
      }

      throw new Error(response?.error || 'Failed to update campaign');
    } catch (error) {
      console.error('[Email Campaigns] Error updating campaign:', error);
      throw error;
    }
  },

  /**
   * Delete a campaign (draft only)
   */
  deleteCampaign: async (campaignId: string): Promise<void> => {
    try {
      console.log('[Email Campaigns] Deleting campaign:', campaignId);

      const response = await apiRequest<ApiResponse>({
        method: 'DELETE',
        url: `/api/email/campaigns/${campaignId}`
      });

      if (response && response.success) {
        console.log('[Email Campaigns] Campaign deleted successfully');
        return;
      }

      throw new Error(response?.error || 'Failed to delete campaign');
    } catch (error) {
      console.error('[Email Campaigns] Error deleting campaign:', error);
      throw error;
    }
  },

  /**
   * Send a campaign (queue emails)
   */
  sendCampaign: async (
    campaignId: string,
    options?: {
      confirm?: boolean;
      test_send?: boolean;
    }
  ): Promise<{ campaign_id: string; status: string; emails_queued: number }> => {
    try {
      console.log('[Email Campaigns] Sending campaign:', campaignId);

      const response = await apiRequest<ApiResponse<{
        campaign_id: string;
        status: string;
        emails_queued: number;
      }>>({
        method: 'POST',
        url: `/api/email/campaigns/${campaignId}/send`,
        data: options || { confirm: true }
      });

      if (response && response.success && response.data) {
        console.log('[Email Campaigns] Campaign sent/queued successfully');
        return response.data;
      }

      throw new Error(response?.error || 'Failed to send campaign');
    } catch (error) {
      console.error('[Email Campaigns] Error sending campaign:', error);
      throw error;
    }
  },

  /**
   * Preview campaign audience (get recipient count and samples)
   */
  previewAudience: async (
    organisationId: string,
    filters: {
      restaurant_ids?: string[];
      audience_type?: 'all' | 'filtered' | 'custom';
      audience_filters?: Record<string, any>;
    }
  ): Promise<AudiencePreview> => {
    try {
      console.log('[Email Campaigns] Previewing audience for organisation:', organisationId, 'restaurants:', filters.restaurant_ids);

      const response = await apiRequest<ApiResponse<AudiencePreview>>({
        method: 'POST',
        url: '/api/email/campaigns/preview-audience',
        data: {
          organisation_id: organisationId,
          ...filters
        }
      });

      if (response && response.success && response.data) {
        console.log('[Email Campaigns] Audience preview:', response.data.total_recipients, 'recipients');
        return response.data;
      }

      throw new Error(response?.error || 'Failed to preview audience');
    } catch (error) {
      console.error('[Email Campaigns] Error previewing audience:', error);
      throw error;
    }
  },

  /**
   * Preview campaign with resolved variables for a specific customer
   * Phase 4.2b - Customer Preview with Variable Resolution
   */
  previewWithVariables: async (
    campaignId: string,
    customerId: string,
    organisationId: string
  ): Promise<{
    html: string;
    subject: string;
    resolved_variables: Record<string, Record<string, string>>;
    customer: {
      id: string;
      email: string;
      first_name: string;
      last_name: string;
    };
  }> => {
    try {
      console.log('[Email Campaigns] Generating preview with variables for customer:', customerId);

      const response = await apiRequest<ApiResponse<{
        html: string;
        subject: string;
        resolved_variables: Record<string, Record<string, string>>;
        customer: {
          id: string;
          email: string;
          first_name: string;
          last_name: string;
        };
      }>>({
        method: 'POST',
        url: '/api/email/campaigns/preview-with-variables',
        data: {
          campaign_id: campaignId,
          customer_id: customerId,
          organisation_id: organisationId
        }
      });

      if (response && response.success && response.data) {
        console.log('[Email Campaigns] Preview generated successfully');
        return response.data;
      }

      throw new Error(response?.error || 'Failed to generate preview');
    } catch (error) {
      console.error('[Email Campaigns] Error generating preview:', error);
      throw error;
    }
  },

  /**
   * Send a test email
   */
  sendTestEmail: async (
    campaignId: string,
    testEmail: string
  ): Promise<void> => {
    try {
      console.log('[Email Campaigns] Sending test email to:', testEmail);

      const response = await apiRequest<ApiResponse>({
        method: 'POST',
        url: `/api/email/campaigns/${campaignId}/test`,
        data: { test_email: testEmail }
      });

      if (response && response.success) {
        console.log('[Email Campaigns] Test email sent successfully');
        return;
      }

      throw new Error(response?.error || 'Failed to send test email');
    } catch (error) {
      console.error('[Email Campaigns] Error sending test email:', error);
      throw error;
    }
  },

  /**
   * ==================== TEMPLATE ENDPOINTS ====================
   */

  /**
   * Fetch email templates for an organisation
   */
  fetchTemplates: async (
    organisationId: string,
    filters?: {
      restaurant_id?: string;
      category?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<EmailTemplate[]> => {
    try {
      console.log('[Email Templates] Fetching templates for organisation:', organisationId);

      const params = new URLSearchParams({
        organisation_id: organisationId,
      });

      if (filters?.restaurant_id) params.append('restaurant_id', filters.restaurant_id);
      if (filters?.category) params.append('category', filters.category);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const response = await apiRequest<TemplateListResponse>({
        method: 'GET',
        url: `/api/email/templates?${params.toString()}`
      });

      if (response && response.success && response.templates) {
        console.log(`[Email Templates] Successfully fetched ${response.templates.length} templates`);
        return response.templates;
      }

      throw new Error('Failed to fetch templates');
    } catch (error) {
      console.error('[Email Templates] Error fetching templates:', error);
      throw error;
    }
  },

  /**
   * Get a single template by ID
   */
  getTemplate: async (templateId: string): Promise<EmailTemplate> => {
    try {
      console.log('[Email Templates] Fetching template:', templateId);

      const response = await apiRequest<{ success: boolean; template: EmailTemplate }>({
        method: 'GET',
        url: `/api/email/templates/${templateId}`
      });

      if (response && response.success && response.template) {
        console.log('[Email Templates] Successfully fetched template');
        return response.template;
      }

      throw new Error('Failed to fetch template');
    } catch (error) {
      console.error('[Email Templates] Error fetching template:', error);
      throw error;
    }
  },

  /**
   * Create a new email template
   */
  createTemplate: async (templateData: {
    organisation_id: string;
    restaurant_id?: string | null;
    template_name: string;
    template_subject: string;
    template_content_html: string;
    template_content_text?: string;
    category?: string;
    template_description?: string;
    available_variables?: string[];
    design_json?: Record<string, any> | null;
    is_system_template?: boolean;
    is_active?: boolean;
  }): Promise<EmailTemplate> => {
    try {
      console.log('[Email Templates] Creating template:', templateData.template_name);

      // Validate required fields
      if (!templateData.organisation_id) {
        throw new Error('Organisation ID is required');
      }
      if (!templateData.template_name || !templateData.template_subject) {
        throw new Error('Template name and subject are required');
      }
      if (!templateData.template_content_html) {
        throw new Error('Template content is required');
      }

      const response = await apiRequest<{ success: boolean; template: EmailTemplate; error?: string }>({
        method: 'POST',
        url: '/api/email/templates',
        data: templateData
      });

      if (response && response.success && response.template) {
        console.log('[Email Templates] Template created successfully:', response.template.id);
        return response.template;
      }

      throw new Error(response?.error || 'Failed to create template');
    } catch (error) {
      console.error('[Email Templates] Error creating template:', error);
      throw error;
    }
  },

  /**
   * Update an existing template
   */
  updateTemplate: async (
    templateId: string,
    updates: Partial<EmailTemplate>
  ): Promise<EmailTemplate> => {
    try {
      console.log('[Email Templates] Updating template:', templateId);

      const response = await apiRequest<{ success: boolean; template: EmailTemplate; error?: string }>({
        method: 'PUT',
        url: `/api/email/templates/${templateId}`,
        data: updates
      });

      if (response && response.success && response.template) {
        console.log('[Email Templates] Template updated successfully');
        return response.template;
      }

      throw new Error(response?.error || 'Failed to update template');
    } catch (error) {
      console.error('[Email Templates] Error updating template:', error);
      throw error;
    }
  },

  /**
   * Delete a template
   */
  deleteTemplate: async (templateId: string): Promise<void> => {
    try {
      console.log('[Email Templates] Deleting template:', templateId);

      const response = await apiRequest<ApiResponse>({
        method: 'DELETE',
        url: `/api/email/templates/${templateId}`
      });

      if (response && response.success) {
        console.log('[Email Templates] Template deleted successfully');
        return;
      }

      throw new Error(response?.error || 'Failed to delete template');
    } catch (error) {
      console.error('[Email Templates] Error deleting template:', error);
      throw error;
    }
  },

  /**
   * Duplicate an existing template
   */
  duplicateTemplate: async (templateId: string): Promise<EmailTemplate> => {
    try {
      console.log('[Email Templates] Duplicating template:', templateId);

      const response = await apiRequest<{ success: boolean; template: EmailTemplate; error?: string }>({
        method: 'POST',
        url: `/api/email/templates/${templateId}/duplicate`
      });

      if (response && response.success && response.template) {
        console.log('[Email Templates] Template duplicated successfully:', response.template.id);
        return response.template;
      }

      throw new Error(response?.error || 'Failed to duplicate template');
    } catch (error) {
      console.error('[Email Templates] Error duplicating template:', error);
      throw error;
    }
  },

  /**
   * ==================== SUBSCRIBER ENDPOINTS (Future) ====================
   */

  /**
   * Get recent customers for preview selector
   * Phase 4.2b - Fetch customers for variable resolution preview
   */
  getRecentCustomers: async (
    organisationId: string,
    limit: number = 50
  ): Promise<Array<{
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    total_orders?: number;
  }>> => {
    try {
      console.log('[Email Customers] Fetching recent customers for organisation:', organisationId);

      // Two-query approach: Get recent customer IDs first (uses index), then fetch customer details
      // This avoids JOIN performance issues with the index

      // Step 1: Get recent customer IDs using the index (FAST)
      const { data: activityData, error: activityError } = await supabase
        .from('customer_organisation_activity')
        .select('customer_id, total_orders')
        .eq('organisation_id', organisationId)
        .order('latest_order_date', { ascending: false })
        .limit(limit);

      if (activityError) {
        console.error('[Email Customers] Activity query error:', activityError);
        throw new Error(activityError.message);
      }

      if (!activityData || activityData.length === 0) {
        console.log('[Email Customers] No customers found for organisation:', organisationId);
        return [];
      }

      // Step 2: Fetch customer details for these IDs (FAST - small set)
      const customerIds = activityData.map(a => a.customer_id);

      const { data: customersData, error: customersError } = await supabase
        .from('customers')
        .select('id, email, first_name, last_name')
        .in('id', customerIds);

      if (customersError) {
        console.error('[Email Customers] Customers query error:', customersError);
        throw new Error(customersError.message);
      }

      // Step 3: Merge data in correct order
      const customerMap = new Map(customersData?.map(c => [c.id, c]) || []);

      const customers = activityData
        .map(activity => {
          const customer = customerMap.get(activity.customer_id);
          if (!customer) return null;

          return {
            id: customer.id,
            email: customer.email,
            first_name: customer.first_name,
            last_name: customer.last_name,
            total_orders: activity.total_orders || 0
          };
        })
        .filter(c => c !== null);

      console.log('[Email Customers] Fetched', customers.length, 'recent customers (newest first)');
      return customers;
    } catch (error) {
      console.error('[Email Customers] Error fetching customers:', error);
      throw error;
    }
  },

  /**
   * Get subscriber statistics for an organisation
   * Note: This endpoint may need to be implemented on the backend
   */
  getSubscriberStats: async (organisationId: string): Promise<{
    total: number;
    opted_in: number;
    opted_out: number;
    bounced: number;
  }> => {
    try {
      console.log('[Email Subscribers] Fetching stats for organisation:', organisationId);

      // TODO: Implement backend endpoint for subscriber stats
      // For now, return mock data or throw not implemented error
      throw new Error('Subscriber stats endpoint not yet implemented');
    } catch (error) {
      console.error('[Email Subscribers] Error fetching subscriber stats:', error);
      throw error;
    }
  },

  /**
   * ==================== TEMPLATE VARIABLE SYSTEM (Added 2025-10-22) ====================
   */

  /**
   * Get default template variable values from restaurant context
   * Fetches restaurant colors and logos for template customization
   *
   * @param organisationId - Organisation ID (required)
   * @param restaurantId - Restaurant ID (optional)
   * @returns Default color and logo values
   */
  getTemplateDefaults: async (
    organisationId: string,
    restaurantId?: string | null
  ): Promise<{
    primaryColor: string;
    secondaryColor: string;
    textColor: string;
    headerLogo: string | null;
    footerLogo: string | null;
    restaurantName: string;
  }> => {
    try {
      console.log('[Email Templates] Fetching template defaults for organisation:', organisationId);

      const params = new URLSearchParams({
        organisation_id: organisationId,
      });

      if (restaurantId) {
        params.append('restaurant_id', restaurantId);
      }

      const response = await apiRequest<{
        success: boolean;
        defaults: {
          primaryColor: string;
          secondaryColor: string;
          textColor: string;
          headerLogo: string | null;
          footerLogo: string | null;
          restaurantName: string;
        };
        error?: string;
      }>({
        method: 'GET',
        url: `/api/email/templates/defaults?${params.toString()}`
      });

      if (response && response.success && response.defaults) {
        console.log('[Email Templates] Template defaults fetched successfully');
        return response.defaults;
      }

      throw new Error(response?.error || 'Failed to fetch template defaults');
    } catch (error) {
      console.error('[Email Templates] Error fetching template defaults:', error);
      // Return safe defaults on error
      return {
        primaryColor: '#c5b068',
        secondaryColor: '#890c17',
        headerLogo: null,
        footerLogo: null,
        restaurantName: 'Your Restaurant'
      };
    }
  },

  /**
   * Customize a system template with color and logo variables
   * Applies user-selected colors and logos to a system template's design_json
   *
   * @param templateId - System template ID (UUID)
   * @param variables - Color and logo values to apply
   * @returns Customized design_json ready for GrapesJS
   */
  customizeTemplate: async (
    templateId: string,
    variables: {
      primaryColor?: string;
      secondaryColor?: string;
      primaryTextColor?: string;
      secondaryTextColor?: string;
      headerLogo?: string;
      footerLogo?: string;
    }
  ): Promise<{
    design_json: Record<string, any>;
    template_name: string;
    variables_applied: number;
  }> => {
    try {
      console.log('[Email Templates] Customizing template:', templateId, variables);

      const params = new URLSearchParams();

      if (variables.primaryColor) {
        params.append('primaryColor', variables.primaryColor);
      }
      if (variables.secondaryColor) {
        params.append('secondaryColor', variables.secondaryColor);
      }
      if (variables.primaryTextColor) {
        params.append('primaryTextColor', variables.primaryTextColor);
      }
      if (variables.secondaryTextColor) {
        params.append('secondaryTextColor', variables.secondaryTextColor);
      }
      if (variables.headerLogo) {
        params.append('headerLogo', variables.headerLogo);
      }
      if (variables.footerLogo) {
        params.append('footerLogo', variables.footerLogo);
      }

      const response = await apiRequest<{
        success: boolean;
        design_json: Record<string, any>;
        template_name: string;
        variables_applied: number;
        error?: string;
      }>({
        method: 'GET',
        url: `/api/email/templates/${templateId}/customize?${params.toString()}`
      });

      if (response && response.success && response.design_json) {
        console.log('[Email Templates] Template customized successfully:', response.template_name);
        return {
          design_json: response.design_json,
          template_name: response.template_name,
          variables_applied: response.variables_applied
        };
      }

      throw new Error(response?.error || 'Failed to customize template');
    } catch (error) {
      console.error('[Email Templates] Error customizing template:', error);
      throw error;
    }
  },

  /**
   * Get live HTML preview of template with variables applied
   * Used by TemplateCustomizationModal to show real-time preview
   *
   * @param templateId - Template UUID
   * @param variables - Color and logo variables to apply
   * @returns HTML string ready for iframe rendering
   */
  getTemplatePreview: async (
    templateId: string,
    variables: {
      primaryColor?: string;
      secondaryColor?: string;
      primaryTextColor?: string;
      secondaryTextColor?: string;
      headerLogo?: string | null;
      footerLogo?: string | null;
    }
  ): Promise<{ html: string }> => {
    try {
      console.log('[Email Templates] Generating preview for template:', templateId, variables);

      // Build query string from variables
      const params = new URLSearchParams();
      if (variables.primaryColor) params.append('primaryColor', variables.primaryColor);
      if (variables.secondaryColor) params.append('secondaryColor', variables.secondaryColor);
      if (variables.primaryTextColor) params.append('primaryTextColor', variables.primaryTextColor);
      if (variables.secondaryTextColor) params.append('secondaryTextColor', variables.secondaryTextColor);
      if (variables.headerLogo) params.append('headerLogo', variables.headerLogo);
      if (variables.footerLogo) params.append('footerLogo', variables.footerLogo);

      const response = await apiRequest<{
        success: boolean;
        html: string;
        error?: string;
      }>({
        method: 'GET',
        url: `/api/email/templates/${templateId}/preview?${params.toString()}`,
      });

      if (response && response.success && response.html) {
        console.log('[Email Templates] Preview generated, HTML length:', response.html.length);
        return { html: response.html };
      }

      throw new Error(response?.error || 'Failed to generate preview');
    } catch (error) {
      console.error('[Email Templates] Error generating preview:', error);
      throw error;
    }
  },

  /**
   * Preview template with resolved variables for a specific customer
   * Similar to campaign preview but for templates
   * Uses unified variable-resolution.js service on backend
   */
  previewTemplateWithCustomer: async (
    templateId: string,
    customerId: string,
    organisationId: string
  ): Promise<{
    html: string;
    subject: string;
    resolved_variables: Record<string, Record<string, string>>;
    customer: {
      id: string;
      email: string;
      first_name: string;
      last_name: string;
    };
  }> => {
    try {
      console.log('[Email Templates] Generating preview with customer data:', customerId);

      const response = await apiRequest<ApiResponse<{
        html: string;
        subject: string;
        resolved_variables: Record<string, Record<string, string>>;
        customer: {
          id: string;
          email: string;
          first_name: string;
          last_name: string;
        };
      }>>({
        method: 'POST',
        url: '/api/email/templates/preview-with-customer',
        data: {
          template_id: templateId,
          customer_id: customerId,
          organisation_id: organisationId
        }
      });

      if (response && response.success && response.data) {
        console.log('[Email Templates] Preview generated successfully');
        return response.data;
      }

      throw new Error(response?.error || 'Failed to generate preview');
    } catch (error) {
      console.error('[Email Templates] Error generating preview:', error);
      throw error;
    }
  },

  /**
   * Preview template content (unsaved) with real customer data
   * For templates being edited that don't have an ID yet
   * Uses unified variable-resolution.js service on backend
   */
  previewTemplateContentWithCustomer: async (
    htmlContent: string,
    textContent: string,
    subject: string,
    customerId: string,
    organisationId: string
  ): Promise<{
    html: string;
    subject: string;
    resolved_variables: Record<string, Record<string, string>>;
    customer: {
      id: string;
      email: string;
      first_name: string;
      last_name: string;
    };
  }> => {
    try {
      console.log('[Email Templates] Generating preview for unsaved template with customer data');

      const response = await apiRequest<ApiResponse<{
        html: string;
        subject: string;
        resolved_variables: Record<string, Record<string, string>>;
        customer: {
          id: string;
          email: string;
          first_name: string;
          last_name: string;
        };
      }>>({
        method: 'POST',
        url: '/api/email/templates/preview-content-with-customer',
        data: {
          html_content: htmlContent,
          text_content: textContent,
          subject: subject,
          customer_id: customerId,
          organisation_id: organisationId
        }
      });

      if (response && response.success && response.data) {
        console.log('[Email Templates] Preview generated successfully');
        return response.data;
      }

      throw new Error(response?.error || 'Failed to generate preview');
    } catch (error) {
      console.error('[Email Templates] Error generating preview:', error);
      throw error;
    }
  }
};

export default EmailService;
